import React, { useEffect, useMemo, useState } from "react";
import PatientListModal from "./PatientListModal";
import {
  isPmServiceDetailsLayout,
  collectCptExportCodes,
  getPatientListColumns,
  processPatientData
} from "./PatientListTable";

const CommonPatientListModal = ({
  isOpen,
  onToggle,
  contextMeta,
  statusLabel,
  displayMonth,
  totalApiPatients,
  rawPatients = [],
  showPatientListLoading,
  renderLoading,
  onViewPatientProfile
}) => {
  const normalizePatients = (patientsInput) => {
    if (Array.isArray(patientsInput)) return patientsInput;
    if (!patientsInput || typeof patientsInput !== "object") return [];

    const candidateLists = [
      patientsInput.patients,
      patientsInput.rows,
      patientsInput.list,
      patientsInput.items,
      patientsInput.data
    ];

    const firstArray = candidateLists.find((entry) => Array.isArray(entry));
    return firstArray || [];
  };

  const [patientSearchQuery, setPatientSearchQuery] = useState("");
  const [patientListPage, setPatientListPage] = useState(1);
  const [patientListLimit, setPatientListLimit] = useState(10);

  useEffect(() => {
    if (isOpen) {
      setPatientSearchQuery("");
      setPatientListPage(1);
      setPatientListLimit(10);
    }
  }, [isOpen, contextMeta?.filePrefix, statusLabel]);

  const sortByLastFirstName = (patients) =>
    [...patients].sort((a, b) => {
      const getSortableName = (name) => {
        if (!name) return { lastName: "", firstName: "" };
        const parts = String(name).split(",").map((part) => part.trim());
        return { lastName: parts[0] || "", firstName: parts[1] || "" };
      };

      const nameA = getSortableName(a.patientName);
      const nameB = getSortableName(b.patientName);

      if (nameA.lastName < nameB.lastName) return -1;
      if (nameA.lastName > nameB.lastName) return 1;
      if (nameA.firstName < nameB.firstName) return -1;
      if (nameA.firstName > nameB.firstName) return 1;
      return 0;
    });

  const cptStatusFilter = statusLabel === "MET" ? "met" : statusLabel === "NOT MET" ? "notMet" : null;
  const cptDetailsLayout = contextMeta?.cptDetailsLayout || "default";
  const isClinicalPmLayout = isPmServiceDetailsLayout(cptDetailsLayout);

  const cptExportCodes = useMemo(() => {
    if (showPatientListLoading || isClinicalPmLayout) {
      return [];
    }

    return collectCptExportCodes(normalizePatients(rawPatients), cptStatusFilter);
  }, [rawPatients, showPatientListLoading, statusLabel, isClinicalPmLayout, cptStatusFilter]);

  const patientRows = useMemo(() => {
    if (showPatientListLoading) {
      return [];
    }

    const normalizedPatients = normalizePatients(rawPatients);
    const processedPatients = processPatientData(normalizedPatients, {
      cptExportCodes,
      cptStatusFilter,
      cptDetailsLayout
    });
    const normalizedQuery = patientSearchQuery.toLowerCase().trim();

    if (!normalizedQuery) {
      return sortByLastFirstName(processedPatients);
    }

    const filtered = processedPatients.filter((patient) => {
      const searchFields = [
        patient.patientName,
        patient.email,
        patient.phone,
        patient.organization,
        patient.mainCareProviderName,
        patient.enrolledProgramsLabel,
        patient.insuranceProviderLabel,
        patient.additionalDetailsText,
        patient.clinicalRpmTimeDisplay,
        patient.clinicalCcmTimeDisplay,
        patient.technicalAdherenceDaysDisplay,
        patient.billableCptCodesDisplay
      ].map((field) => field?.toString().toLowerCase() || "");

      return searchFields.some((field) => field.includes(normalizedQuery));
    });

    return sortByLastFirstName(filtered);
  }, [rawPatients, patientSearchQuery, showPatientListLoading, cptExportCodes, statusLabel, cptDetailsLayout, cptStatusFilter]);

  return (
    <PatientListModal
      isOpen={isOpen}
      onToggle={onToggle}
      contextMeta={contextMeta}
      statusLabel={statusLabel}
      displayMonth={displayMonth}
      totalApiPatients={totalApiPatients}
      patientRows={patientRows}
      cptExportCodes={cptExportCodes}
      columns={getPatientListColumns({ onViewPatientProfile, cptDetailsLayout })}
      cptDetailsLayout={cptDetailsLayout}
      showPatientListLoading={showPatientListLoading}
      patientSearchQuery={patientSearchQuery}
      onSearchChange={(e) => {
        setPatientSearchQuery(e.target.value);
        setPatientListPage(1);
      }}
      onClearSearch={() => {
        setPatientSearchQuery("");
        setPatientListPage(1);
      }}
      patientListPage={patientListPage}
      patientListLimit={patientListLimit}
      onPageChange={(page) => setPatientListPage(page)}
      onSizePerPageChange={(sizePerPage, page) => {
        setPatientListLimit(sizePerPage);
        setPatientListPage(page || 1);
      }}
      renderLoading={renderLoading}
    />
  );
};

export default CommonPatientListModal;
